// Builds the images.ambient and images.backgrounds slots for the pmMovie that
// a TMDB match writes. Before this existed the ambient slot was never touched,
// so a movie whose provider ambientURL is "" kept its broken background no
// matter which backdrops were selected.
const toImageObject = (url, alt) => ({
    id: null,
    format: null,
    alt,
    url,
    position: null,
    positionX: 50,
    positionY: 0,
})

// Deliberate divergence from the legacy buildBackgrounds this replaces: that
// code normalized only the existing-PM-backgrounds branch and passed the
// provider-backgrounds fallback through untouched, leaving positionX/positionY
// absent. Here both branches run through this, so provider backgrounds copied
// into pmMovie always arrive with explicit positionX/positionY — which means
// they get focus-point sliders in the edit grid instead of none.
const normalizeBackground = background => ({
    ...background,
    positionX: background.positionX !== undefined ? background.positionX : null,
    positionY: background.positionY !== undefined ? background.positionY : null,
})

// 'replace' is an explicit overwrite, so it may replace a working provider
// ambient or an existing PM ambient. 'add' must leave a working ambient alone
// and only repair one that has no url — which is read from the *merged* value,
// because a working provider ambient has an empty PM slot too.
export const shouldWriteAmbient = (mergeMode, effectiveAmbientUrl) =>
    mergeMode === 'replace' || !effectiveAmbientUrl

export const buildTmdbImageSlots = ({
    movieTitle,
    selectedUrls,
    mergeMode,
    effectiveAmbientUrl,
    existingPmBackgrounds,
    providerBackgrounds,
}) => {
    const urls = selectedUrls || []
    const writeAmbient = urls.length > 0 && shouldWriteAmbient(mergeMode, effectiveAmbientUrl)

    const ambient = writeAmbient ? toImageObject(urls[0], `${movieTitle} - Ambient`) : null
    // The url used as ambient must not also sit in backgrounds — one url, one slot.
    const backdropUrls = writeAmbient ? urls.slice(1) : urls
    const newBackgrounds = backdropUrls.map(url => toImageObject(url, `${movieTitle} - Background`))

    if (mergeMode === 'replace') {
        // Deliberate exception to the "one url, one slot" rule above: an empty PM
        // backgrounds list cannot express "no backgrounds" — that is a property of
        // the merge (empty counts as unset, so it falls back to the provider list),
        // not a choice the admin can make. With exactly one selected url the ambient
        // consumes it and newBackgrounds would come out empty, resurrecting the
        // provider's own backgrounds. Keeping the promoted url in backgrounds too is
        // the only way "replace everything with just this one image" can be expressed.
        const backgrounds = writeAmbient && newBackgrounds.length === 0 && urls.length > 0
            ? [toImageObject(urls[0], `${movieTitle} - Background`)]
            : newBackgrounds
        return { ambient, backgrounds }
    }

    const existing = existingPmBackgrounds && existingPmBackgrounds.length > 0
        ? existingPmBackgrounds
        : (providerBackgrounds || [])

    return { ambient, backgrounds: [...newBackgrounds, ...existing.map(normalizeBackground)] }
}

export default buildTmdbImageSlots
